/**
 * Animated lesson visuals — SVG-based, themed to match each narration block.
 * Switch on visual_id; renders a fullscreen visual that animates while the instructor speaks.
 */
import { useEffect, useState } from "react";

const C = {
  amber: "#F5A623",
  cyan: "#00E0FF",
  rose: "#FF2A6D",
  cream: "#0F172A",
  dark: "#0B0716",
  panel: "#14102A",
  green: "#22C55E",
  red: "#EF4444",
};

export default function LessonVisual({ visualId, blockIndex, lesson }) {
  // Each visual remounts when the block changes, restarting animations.
  const key = `${visualId}-${blockIndex}`;
  switch (visualId) {
    case "intro":             return <Intro key={key} lesson={lesson} />;;
    case "lift_formula":      return <LiftFormula key={key} />;
    case "critical_angle":    return <CriticalAngle key={key} />;
    case "c172_speeds":       return <C172Speeds key={key} />;
    case "factors":           return <Factors key={key} />;
    case "symptoms":          return <Symptoms key={key} />;
    case "warning_devices":   return <WarningDevices key={key} />;
    case "power_recovery":    return <PowerRecovery key={key} />;
    case "wing_drop":         return <WingDrop key={key} />;
    case "effect_power":      return <EffectCompare key={key} title="Effect of POWER (1800 RPM)" rows={[
      ["Onset time to stall", "Longer", C.green],
      ["Nose attitude at stall", "Higher", C.amber],
      ["Airspeed at stall", "Lower", C.amber],
      ["Wing drop chance", "Increased", C.red],
    ]} caption="Prop wash adds vertical lift component." />;
    case "effect_flap":       return <EffectCompare key={key} title="Effect of FLAP" rows={[
      ["Onset time to stall", "Shorter", C.red],
      ["Nose attitude at stall", "Lower", C.amber],
      ["Airspeed at stall", "Lower", C.amber],
      ["Wing drop chance", "Slightly ↑", C.red],
    ]} caption="Flap raises CL but lowers critical AoA." />;
    case "effect_flap_power": return <EffectCompare key={key} title="Effect of FLAP + POWER" rows={[
      ["Onset time", "Normal (same as clean)", C.cyan],
      ["Nose attitude", "Normal (same as clean)", C.cyan],
      ["Airspeed at stall", "MUCH lower", C.amber],
      ["Wing drop chance", "Increased", C.red],
    ]} caption="Lowest stall speed — always be ready with rudder." />;
    case "scenarios":         return <Scenarios key={key} />;
    case "hasell":            return <HASELL key={key} />;
    case "stall_entry":       return <StallEntry key={key} />;
    case "mantra":            return <Mantra key={key} />;
    /* ---- Turns lesson visuals ---- */
    case "level_turn_forces": return <LevelTurnForces key={key} />;
    case "adverse_yaw":       return <AdverseYaw key={key} />;
    case "balance_bank_back": return <BalanceBankBack key={key} />;
    case "spiral_dive":       return <SpiralDive key={key} />;
    case "steep_45":          return <SteepBank key={key} bank={45} />;
    case "stall_speed_curve": return <StallSpeedCurve key={key} />;
    case "power_sandwich":    return <PowerSandwich key={key} />;
    case "descending_steep":  return <DescendingSteep key={key} />;
    default:                  return <Default key={key} />;
  }
}

/* ----------------- Helpers ----------------- */
const Frame = ({ children, label }) => (
  <div className="relative w-full h-full flex flex-col">
    {label && <div className="hud-label text-[var(--brand-primary)] mb-3">{label}</div>}
    <div className="relative flex-1 instrument-panel hud-border overflow-hidden">
      {children}
    </div>
  </div>
);

function useStep(total, intervalMs = 1100) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % total), intervalMs);
    return () => clearInterval(t);
  }, [total, intervalMs]);
  return step;
}

/* ----------------- Visuals ----------------- */

function Intro({ lesson }) {
  const title = (lesson?.title || "MISSION BRIEF").toUpperCase();
  const subtitle = (lesson?.subtitle || "RECOGNITION · RECOVERY · PREVENTION").toUpperCase();
  const objectivesCount = lesson?.objectives?.length || 0;
  const blocksCount = lesson?.blocks?.length || 0;
  const minutes = lesson?.duration_minutes || 0;
  const tiles = [
    `${objectivesCount} OBJECTIVES`,
    `${blocksCount} BLOCKS`,
    `${minutes} MIN`,
  ];
  // Scale title down if very long so it fits the panel
  const titleSize = title.length > 16 ? "text-5xl" : title.length > 10 ? "text-6xl" : "text-7xl";
  return (
    <Frame label={`MISSION BRIEF // ${title}`}>
      <div className="absolute inset-0 flex items-center justify-center p-8">
        <div className="text-center">
          <div className={`font-heading ${titleSize} text-[var(--brand-primary)] tracking-wider animate-pulse`}>{title}</div>
          <div className="mt-3 font-heading text-2xl text-[var(--brand-primary)]">{subtitle}</div>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-2xl mx-auto">
            {tiles.map((t, i) => (
              <div key={i} className="bracket relative p-3 text-center"><span className="bl"/><span className="br"/>
                <div className="font-mono text-[var(--brand-primary)] text-lg">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Frame>
  );
}

function LiftFormula() {
  // Highlight CL → ρ → V² → S in sequence
  const step = useStep(5, 2000);
  const tokens = [
    { id: "L", label: "L", sub: "Lift",          color: C.amber },
    { id: "=", label: "=", sub: "",              color: C.cream },
    { id: "CL", label: "Cʟ", sub: "AoA / Camber", color: C.rose },
    { id: "½ρ", label: "½ρ", sub: "Air Density",  color: C.cyan },
    { id: "V²", label: "V²", sub: "Velocity²",     color: C.amber },
    { id: "S",  label: "S",  sub: "Wing Area",    color: C.green },
  ];
  // Index of highlighted variable (skip "L" and "=")
  const highlightId = ["CL", "½ρ", "V²", "S", null][step];
  return (
    <Frame label="REVISION // LIFT EQUATION">
      <div className="absolute inset-0 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="flex items-baseline justify-center gap-3 flex-wrap">
            {tokens.map((t) => {
              const isHL = highlightId === t.id;
              return (
                <div key={t.id} className="flex flex-col items-center transition-all duration-500" style={{ transform: isHL ? "translateY(-8px)" : "none" }}>
                  <div
                    className="font-heading font-bold text-7xl transition-all duration-500"
                    style={{ color: isHL ? t.color : "#FFFFFF", textShadow: isHL ? `0 0 28px ${t.color}` : "none" }}
                  >
                    {t.label}
                  </div>
                  {t.sub && (
                    <div className="hud-label mt-2 transition-all duration-500" style={{ color: isHL ? t.color : "#6F6840", opacity: isHL ? 1 : 0.5 }}>{t.sub}</div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-10 text-slate-900 text-lg font-mono" style={{ color: C.cream }}>
            <span style={{ color: C.amber }}>LIFT</span> = <span style={{ color: C.cream }}>WEIGHT</span>
            <span className="ml-3 text-slate-500 text-sm">// in straight & level</span>
          </div>
        </div>
      </div>
    </Frame>
  );
}

function CriticalAngle() {
  // wing angle increases 4 → 10 → 15 → 20 (stalled)
  const aoa = useStep(4, 1500);
  const angles = [4, 10, 15, 20];
  const angle = angles[aoa];
  const stalled = angle >= 15;
  return (
    <Frame label={`AOA // ${angle}° ${stalled ? '— STALLED' : ''}`}>
      <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-2 p-6 gap-4">
        {/* Wing visualization */}
        <div className="relative bg-white border border-slate-200 overflow-hidden">
          <svg viewBox="0 0 400 300" className="w-full h-full">
            {/* relative airflow lines */}
            {[60, 100, 140, 180, 220].map((y, i) => (
              <line key={i} x1="0" y1={y} x2="400" y2={y} stroke={C.cyan} strokeWidth="1" strokeDasharray="4 6" opacity={0.4 - (stalled ? 0.2 : 0)} />
            ))}
            {/* wing — pivots around centre */}
            <g transform={`translate(200,150) rotate(${-angle})`}>
              <path d="M -110,0 Q -90,-18 0,-16 Q 90,-12 110,0 Q 0,8 -110,0 Z"
                    fill={stalled ? "#5C1A1A" : "#2B2745"} stroke={stalled ? C.red : C.amber} strokeWidth="2" />
              <text x="0" y="28" textAnchor="middle" fontSize="11" fontFamily="JetBrains Mono" fill={stalled ? C.red : C.amber}>{angle}°</text>
            </g>
            {/* turbulent separation when stalled */}
            {stalled && (
              <g opacity="0.9">
                {[0, 1, 2, 3, 4].map((i) => (
                  <circle key={i} cx={130 + i * 25} cy={120 + (i % 2) * 18} r={6 + i * 1.5}
                          fill="none" stroke={C.red} strokeWidth="1.5" opacity={0.8 - i * 0.12}>
                    <animate attributeName="r" values={`${5 + i};${12 + i};${5 + i}`} dur="1.2s" repeatCount="indefinite" />
                  </circle>
                ))}
              </g>
            )}
            <text x="20" y="20" fontSize="10" fontFamily="JetBrains Mono" fill={C.cream} opacity="0.6">RELATIVE AIRFLOW →</text>
          </svg>
        </div>
        {/* Lift curve */}
        <div className="relative bg-white border border-slate-200 overflow-hidden p-4">
          <div className="hud-label mb-1">CL vs ANGLE OF ATTACK</div>
          <svg viewBox="0 0 300 240" className="w-full h-[calc(100%-1.5rem)]">
            {/* axes */}
            <line x1="40" y1="200" x2="280" y2="200" stroke={C.cream} opacity="0.4" />
            <line x1="40" y1="200" x2="40" y2="20" stroke={C.cream} opacity="0.4" />
            <text x="290" y="205" fontSize="10" fontFamily="JetBrains Mono" fill={C.cream} opacity="0.5">AoA</text>
            <text x="20" y="20" fontSize="10" fontFamily="JetBrains Mono" fill={C.cream} opacity="0.5">CL</text>
            {/* critical angle line at 15° → x position */}
            <line x1="190" y1="20" x2="190" y2="200" stroke={C.red} strokeDasharray="4 4" opacity="0.6" />
            <text x="194" y="34" fontSize="10" fontFamily="JetBrains Mono" fill={C.red}>CRITICAL 15°</text>
            {/* curve: rises to 15° then drops */}
            <path d="M 40,200 Q 110,160 190,50 Q 230,70 270,140" stroke={C.amber} strokeWidth="2.5" fill="none" />
            {/* moving point at current AoA */}
            <circle cx={40 + (angle / 20) * 200} cy={angle <= 15 ? 200 - (angle / 15) * 150 : 50 + ((angle - 15) / 5) * 90} r="6" fill={stalled ? C.red : C.amber}>
              <animate attributeName="r" values="6;10;6" dur="0.8s" repeatCount="indefinite" />
            </circle>
          </svg>
        </div>
      </div>
    </Frame>
  );
}

function C172Speeds() {
  return (
    <Frame label="C172 // STALL SPEEDS">
      <div className="absolute inset-0 grid grid-cols-2 gap-4 p-6">
        <Gauge label="Vs1 — CLEAN" speed={48} max={120} color={C.amber} sublabel="Power off, flaps up" />
        <Gauge label="Vs0 — FULL FLAP" speed={40} max={120} color={C.rose} sublabel="Power off, full flap" />
      </div>
    </Frame>
  );
}

function Gauge({ label, speed, max, color, sublabel }) {
  // needle: 0..max maps to -135..+135 degrees
  const angle = -135 + (speed / max) * 270;
  return (
    <div className="relative bg-white border border-slate-200 flex flex-col items-center justify-center p-4">
      <div className="hud-label" style={{ color }}>{label}</div>
      <svg viewBox="0 0 200 200" className="w-full max-w-[260px] mt-2">
        <circle cx="100" cy="100" r="88" fill="#09061A" stroke={color} strokeWidth="2" />
        <circle cx="100" cy="100" r="78" fill="none" stroke={C.cream} strokeWidth="0.5" opacity="0.2" />
        {Array.from({ length: 13 }).map((_, i) => {
          const a = (-135 + (i * 270) / 12) * Math.PI / 180;
          const x1 = 100 + Math.cos(a) * 70;
          const y1 = 100 + Math.sin(a) * 70;
          const x2 = 100 + Math.cos(a) * 82;
          const y2 = 100 + Math.sin(a) * 82;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={C.cream} strokeWidth="1" opacity="0.5" />;
        })}
        {/* arc: red/yellow/green airspeed bands */}
        <path d={describeArc(100,100,72,-135,-105)} stroke={C.red} strokeWidth="6" fill="none" />
        <path d={describeArc(100,100,72,-105,-30)} stroke={C.amber} strokeWidth="6" fill="none" />
        <path d={describeArc(100,100,72,-30,90)} stroke={C.green} strokeWidth="6" fill="none" />
        {/* needle */}
        <g style={{ transform: `rotate(${angle}deg)`, transformOrigin: "100px 100px", transition: "transform 1.5s cubic-bezier(0.34,1.56,0.64,1)" }}>
          <line x1="100" y1="100" x2="100" y2="35" stroke={color} strokeWidth="3" />
          <circle cx="100" cy="100" r="6" fill={color} />
        </g>
        <text x="100" y="160" textAnchor="middle" fontSize="22" fontFamily="JetBrains Mono" fontWeight="700" fill={color}>{speed}</text>
        <text x="100" y="178" textAnchor="middle" fontSize="9" fontFamily="JetBrains Mono" fill={C.cream} opacity="0.6">KNOTS IAS</text>
      </svg>
      <div className="hud-label mt-2">{sublabel}</div>
    </div>
  );
}

function describeArc(cx, cy, r, startAngleDeg, endAngleDeg) {
  const polar = (a) => {
    const rad = a * Math.PI / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };
  const start = polar(startAngleDeg);
  const end = polar(endAngleDeg);
  const large = endAngleDeg - startAngleDeg <= 180 ? "0" : "1";
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 1 ${end.x} ${end.y}`;
}

function Factors() {
  const factors = [
    { icon: "↑", label: "WEIGHT",      effect: "+ stall speed", color: C.red },
    { icon: "↺", label: "BANK ANGLE",  effect: "+ stall speed", color: C.red },
    { icon: "❄", label: "ICE / DAMAGE", effect: "+ stall speed", color: C.red },
    { icon: "↓", label: "FLAPS",       effect: "− stall speed", color: C.green },
  ];
  return (
    <Frame label="FACTORS // STALL SPEED">
      <div className="absolute inset-0 grid grid-cols-2 gap-3 p-6">
        {factors.map((f, i) => (
          <div key={i} className="instrument-panel p-5 flex items-center gap-4 border" style={{ borderColor: f.color + "40", animation: `slideIn 0.5s ${i * 0.15}s both` }}>
            <div className="font-heading text-6xl" style={{ color: f.color, textShadow: `0 0 24px ${f.color}` }}>{f.icon}</div>
            <div>
              <div className="font-heading text-2xl text-slate-900">{f.label}</div>
              <div className="hud-label mt-1" style={{ color: f.color }}>{f.effect}</div>
            </div>
          </div>
        ))}
      </div>
      <style>{`@keyframes slideIn { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }`}</style>
    </Frame>
  );
}

function Symptoms() {
  const items = [
    { label: "AIRSPEED", value: "↓ DECREASING", color: C.red },
    { label: "NOSE ATTITUDE", value: "↑ HIGH", color: C.amber },
    { label: "POWER", value: "IDLE / QUIET", color: C.cyan },
    { label: "CONTROLS", value: "SLOPPY", color: C.amber },
    { label: "STALL HORN", value: "★ ACTIVE", color: C.rose },
    { label: "BUFFET", value: "★ TACTILE", color: C.rose },
  ];
  return (
    <Frame label="SLOW FLIGHT // SYMPTOM CHECK">
      <div className="absolute inset-0 grid grid-cols-2 md:grid-cols-3 gap-3 p-6">
        {items.map((it, i) => (
          <div key={i} className="instrument-panel p-4 flex flex-col" style={{ animation: `pulseIn 0.4s ${i * 0.12}s both`, borderColor: it.color + "30" }}>
            <div className="hud-label">{it.label}</div>
            <div className="font-heading text-2xl mt-2" style={{ color: it.color }}>{it.value}</div>
          </div>
        ))}
      </div>
      <style>{`@keyframes pulseIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }`}</style>
    </Frame>
  );
}

function WarningDevices() {
  return (
    <Frame label="WARNING DEVICES">
      <div className="absolute inset-0 grid grid-cols-3 gap-4 p-6">
        <DeviceCard icon="🔊" iconText="AURAL" title="STALL HORN" desc="Activates a few knots before Vs" color={C.rose} pulse />
        <DeviceCard icon="👁" iconText="VISUAL" title="AoA INDICATOR" desc="Direct AoA, advanced aircraft" color={C.cyan} />
        <DeviceCard icon="≋" iconText="TACTILE" title="BUFFET" desc="Pre-stall airframe vibration" color={C.amber} />
      </div>
    </Frame>
  );
}

function DeviceCard({ icon, iconText, title, desc, color, pulse }) {
  return (
    <div className="instrument-panel p-5 flex flex-col items-center text-center" style={{ borderColor: color + "40" }}>
      <div className={`text-5xl mb-2 ${pulse ? "pulse-amber" : ""}`} style={{ color }}>{icon}</div>
      <div className="hud-label" style={{ color }}>{iconText}</div>
      <div className="font-heading text-xl mt-2" style={{ color: C.cream }}>{title}</div>
      <div className="hud-label mt-2">{desc}</div>
    </div>
  );
}

function PowerRecovery() {
  const step = useStep(3, 1600);
  const steps = [
    { n: "01", word: "RELAX", sub: "the back pressure", detail: "Reduces angle of attack — breaks the stall", color: C.amber },
    { n: "02", word: "MAX",   sub: "full power",         detail: "Throttle forward — minimise altitude loss",   color: C.rose },
    { n: "03", word: "RUDDER", sub: "opposite to yaw",    detail: "Coordinated, prevents wing drop",             color: C.cyan },
  ];
  return (
    <Frame label="POWER-ON RECOVERY">
      <div className="absolute inset-0 flex flex-col justify-center gap-4 p-8">
        {steps.map((s, i) => {
          const isActive = step === i;
          return (
            <div key={i} className="flex items-center gap-6 transition-all duration-700"
                 style={{ opacity: isActive ? 1 : 0.35, transform: isActive ? "translateX(0)" : "translateX(-8px)" }}>
              <div className="font-heading text-5xl" style={{ color: s.color, textShadow: isActive ? `0 0 24px ${s.color}` : "none" }}>{s.n}</div>
              <div className="flex-1">
                <div className="flex items-baseline gap-3">
                  <div className="font-heading text-5xl" style={{ color: isActive ? s.color : C.cream }}>{s.word}</div>
                  <div className="font-mono text-sm" style={{ color: C.cream, opacity: 0.6 }}>{s.sub}</div>
                </div>
                <div className="hud-label mt-1" style={{ color: isActive ? s.color : "#6F6840" }}>{s.detail}</div>
              </div>
              <div className="font-mono text-3xl" style={{ color: isActive ? s.color : "transparent" }}>→</div>
            </div>
          );
        })}
        <div className="mt-4 text-center hud-label" style={{ color: C.amber }}>THEN REGAIN LOST ALTITUDE</div>
      </div>
    </Frame>
  );
}

function WingDrop() {
  // animate: tilt → recover
  const step = useStep(4, 1400);
  const stages = [
    { tilt: 0,   label: "STALL ENTRY", color: C.amber },
    { tilt: -25, label: "LEFT WING DROP", color: C.red },
    { tilt: -25, label: "RELAX BACK PRESSURE → RUDDER", color: C.cyan },
    { tilt: 0,   label: "WINGS LEVEL → RAISE NOSE → POWER", color: C.green },
  ];
  const cur = stages[step];
  return (
    <Frame label={`WING DROP // ${cur.label}`}>
      <div className="absolute inset-0 flex items-center justify-center p-6">
        <svg viewBox="0 0 500 280" className="w-full max-w-3xl">
          {/* horizon */}
          <line x1="0" y1="160" x2="500" y2="160" stroke={C.cream} opacity="0.2" strokeDasharray="6 8" />
          {/* aircraft (top down) */}
          <g style={{ transform: `rotate(${cur.tilt}deg)`, transformOrigin: "250px 160px", transition: "transform 1s cubic-bezier(0.34,1.56,0.64,1)" }}>
            {/* fuselage */}
            <ellipse cx="250" cy="160" rx="14" ry="60" fill={C.panel} stroke={cur.color} strokeWidth="2" />
            {/* wings */}
            <rect x="120" y="150" width="260" height="20" fill={C.panel} stroke={cur.color} strokeWidth="2" />
            {/* tail */}
            <rect x="225" y="208" width="50" height="14" fill={C.panel} stroke={cur.color} strokeWidth="2" />
            {/* nose */}
            <polygon points="250,90 240,120 260,120" fill={cur.color} />
          </g>
          {/* indicators */}
          <text x="20" y="30" fontSize="12" fontFamily="JetBrains Mono" fill={cur.color}>{cur.label}</text>
        </svg>
      </div>
      <div className="absolute bottom-3 left-0 right-0 text-center hud-label text-[var(--brand-primary)]">NEVER PICK UP A DROPPED WING WITH AILERON</div>
    </Frame>
  );
}

function EffectCompare({ title, rows, caption }) {
  return (
    <Frame label={title}>
      <div className="absolute inset-0 flex flex-col p-6">
        <div className="flex-1 grid grid-cols-1 gap-2">
          {rows.map(([metric, value, color], i) => (
            <div key={i} className="instrument-panel flex items-center justify-between px-5 py-3" style={{ borderColor: color + "30", animation: `slideIn 0.4s ${i * 0.12}s both` }}>
              <div className="hud-label">{metric}</div>
              <div className="font-heading text-2xl" style={{ color }}>{value}</div>
            </div>
          ))}
        </div>
        {caption && <div className="hud-label text-center mt-3" style={{ color: C.amber }}>{caption}</div>}
      </div>
      <style>{`@keyframes slideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </Frame>
  );
}

function Scenarios() {
  return (
    <Frame label="WHERE STALLS CATCH PILOTS">
      <div className="absolute inset-0 grid md:grid-cols-2 gap-3 p-6">
        <div className="instrument-panel p-5 border border-red-200">
          <div className="hud-label text-red-600">SCENARIO 1</div>
          <div className="font-heading text-2xl mt-2 text-slate-900">TAKEOFF · GO-AROUND</div>
          <svg viewBox="0 0 300 140" className="mt-3 w-full">
            <line x1="0" y1="130" x2="300" y2="130" stroke={C.cream} opacity="0.3" />
            <path d="M 20,130 Q 150,130 280,40" stroke={C.rose} strokeWidth="2.5" fill="none" strokeDasharray="6 4" />
            <polygon points="280,40 270,48 285,52" fill={C.rose} />
          </svg>
          <ul className="hud-label mt-3 space-y-1">
            <li>· LOW AIRSPEED</li>
            <li>· HIGH AoA</li>
            <li>· HIGH NOSE ATTITUDE</li>
          </ul>
        </div>
        <div className="instrument-panel p-5 border border-red-200">
          <div className="hud-label text-red-600">SCENARIO 2</div>
          <div className="font-heading text-2xl mt-2 text-slate-900">BASE TO FINAL TURN</div>
          <svg viewBox="0 0 300 140" className="mt-3 w-full">
            <line x1="60" y1="20" x2="60" y2="120" stroke={C.cream} opacity="0.3" />
            <line x1="60" y1="120" x2="260" y2="120" stroke={C.cream} opacity="0.3" />
            <path d="M 60,40 Q 60,120 240,120" stroke={C.rose} strokeWidth="2.5" fill="none" strokeDasharray="6 4" />
            <polygon points="240,120 230,114 230,126" fill={C.rose} />
            <text x="80" y="60" fontSize="9" fontFamily="JetBrains Mono" fill={C.amber}>OVER-BANK ↺</text>
          </svg>
          <ul className="hud-label mt-3 space-y-1">
            <li>· OVER-BANK ↑ LOAD FACTOR</li>
            <li>· LOW AIRSPEED · HIGH AoA</li>
            <li>· WING DROPS INTO TURN</li>
          </ul>
        </div>
      </div>
    </Frame>
  );
}

function HASELL() {
  const items = [
    ["H", "HEIGHT",    "Recovery + 3000 ft margin"],
    ["A", "AIRFRAME",  "Flaps, gear as required"],
    ["S", "SECURITY",  "Harnesses tight, items stowed"],
    ["E", "ENGINE",    "Temps, pressures, mixture, fuel pump"],
    ["L", "LOCATION",  "Clear of airports, built-up areas, cloud"],
    ["L", "LOOKOUT",   "90° each way or 180° clearing turn"],
  ];
  const step = useStep(items.length + 1, 900);
  return (
    <Frame label="PRE-STALL CHECKS">
      <div className="absolute inset-0 grid grid-cols-1 gap-2 p-6 overflow-auto">
        {items.map(([letter, name, desc], i) => {
          const done = step > i;
          return (
            <div key={i} className="flex items-center gap-4 instrument-panel px-4 py-2 transition-all duration-500" style={{ borderColor: done ? C.amber + "60" : "rgba(255,255,255,0.08)", opacity: done ? 1 : 0.5 }}>
              <div className="font-heading text-3xl w-10 text-center" style={{ color: done ? C.amber : C.cream, textShadow: done ? `0 0 14px ${C.amber}` : "none" }}>{letter}</div>
              <div className="flex-1">
                <div className="font-heading text-xl" style={{ color: done ? C.cream : "#6F6840" }}>{name}</div>
                <div className="hud-label">{desc}</div>
              </div>
              {done && <div className="text-green-400 font-mono text-lg">✓</div>}
            </div>
          );
        })}
      </div>
    </Frame>
  );
}

function StallEntry() {
  return (
    <Frame label="STALL ENTRY TECHNIQUE">
      <div className="absolute inset-0 p-6 flex flex-col gap-3">
        {[
          ["1", "PICK", "Reference point + reference height"],
          ["2", "RAISE", "Progressively raise the nose"],
          ["3", "REDUCE", "Power to idle"],
          ["4", "BALANCE", "Use rudder to stay coordinated"],
          ["5", "STALL", "Sink · Nose drop · Possible wing drop"],
          ["6", "RECOVER", "Initiate recovery procedure"],
        ].map(([n, w, d], i) => (
          <div key={i} className="flex items-center gap-4 instrument-panel px-4 py-2" style={{ animation: `slideIn 0.4s ${i * 0.1}s both` }}>
            <div className="font-heading text-2xl text-[var(--brand-primary)] w-8 text-center">{n}</div>
            <div className="font-heading text-xl text-slate-900 w-32">{w}</div>
            <div className="hud-label flex-1">{d}</div>
          </div>
        ))}
      </div>
      <style>{`@keyframes slideIn { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }`}</style>
    </Frame>
  );
}

function Mantra() {
  return (
    <Frame label="THE MANTRA">
      <div className="absolute inset-0 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="hud-label text-red-600 mb-4">YOUR DEFENCE ★</div>
          <div className="font-heading text-7xl text-[var(--brand-primary)] tracking-wider drop-shadow-2xl" style={{ textShadow: `0 0 40px ${C.amber}` }}>
            RELAX
          </div>
          <div className="font-mono text-slate-500 my-2">·</div>
          <div className="font-heading text-7xl text-red-600 tracking-wider drop-shadow-2xl" style={{ textShadow: `0 0 40px ${C.rose}` }}>
            MAX
          </div>
          <div className="font-mono text-slate-500 my-2">·</div>
          <div className="font-heading text-7xl text-[var(--brand-primary)] tracking-wider drop-shadow-2xl" style={{ textShadow: `0 0 40px ${C.cyan}` }}>
            RUDDER
          </div>
          <div className="mt-6 font-heading text-slate-700 text-lg">RECOGNITION · INSTINCT · RECOVERY</div>
        </div>
      </div>
    </Frame>
  );
}

function Default() {
  return (
    <Frame label="BRIEF IN PROGRESS">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-slate-500 font-mono text-sm blink-cursor">Listening to instructor</div>
      </div>
    </Frame>
  );
}


/* ===================== TURNS VISUALS ===================== */

/* Aircraft viewed from behind, banked. Used by both level & steep visuals. */
function PlaneFromBehind({ bank = 30, size = 220, color = C.amber, glow = true }) {
  return (
    <g transform={`rotate(${-bank} 0 0)`} style={{ transition: "transform 0.7s ease" }}>
      {/* fuselage */}
      <ellipse cx="0" cy="0" rx="10" ry="14" fill="#0b0b0d" stroke={color} strokeWidth="1.6" />
      {/* wings */}
      <rect x={-size/2} y="-3" width={size} height="6" fill={color} stroke={color} strokeWidth="1" rx="2" />
      {/* tail */}
      <rect x="-3" y="-22" width="6" height="14" fill={color} />
      {/* prop disc hint */}
      <ellipse cx="0" cy="-14" rx="14" ry="2" fill="none" stroke={color} strokeWidth="0.6" opacity="0.5" />
      {glow && <circle cx="0" cy="0" r={size/2+4} fill="none" stroke={color} strokeWidth="0.4" opacity="0.25" />}
    </g>
  );
}

function LevelTurnForces() {
  // Lift vector tilts with the bank; vertical component (Lv) must equal Weight.
  const bank = 30; // degrees
  const tilt = (bank * Math.PI) / 180;
  const liftMag = 140;
  const liftEndX = Math.sin(tilt) * liftMag;
  const liftEndY = -Math.cos(tilt) * liftMag;
  return (
    <Frame label="FORCES // LEVEL TURN (30° BANK)">
      <div className="absolute inset-0 flex items-center justify-center">
        <svg viewBox="-220 -180 440 360" className="w-full h-full">
          {/* Horizon */}
          <line x1="-220" y1="0" x2="220" y2="0" stroke={C.cyan} strokeOpacity="0.3" strokeDasharray="4 4" />
          {/* Weight (always straight down) */}
          <line x1="0" y1="0" x2="0" y2="120" stroke={C.rose} strokeWidth="2.5" markerEnd="url(#arrW)" />
          <text x="6" y="118" fill={C.rose} fontSize="14" fontFamily="ui-monospace, monospace">W (weight)</text>
          {/* Total lift (perpendicular to wings) */}
          <line x1="0" y1="0" x2={liftEndX} y2={liftEndY} stroke={C.amber} strokeWidth="3" markerEnd="url(#arrL)" />
          <text x={liftEndX + 6} y={liftEndY} fill={C.amber} fontSize="14" fontFamily="ui-monospace, monospace">L (total lift)</text>
          {/* Vertical component */}
          <line x1="0" y1="0" x2="0" y2={liftEndY} stroke={C.green} strokeWidth="2" strokeDasharray="5 4" />
          <text x="-90" y={liftEndY + 10} fill={C.green} fontSize="11" fontFamily="ui-monospace, monospace">L·cos(φ) = W</text>
          {/* Horizontal component (centripetal) */}
          <line x1="0" y1={liftEndY} x2={liftEndX} y2={liftEndY} stroke={C.cyan} strokeWidth="2" strokeDasharray="5 4" />
          <text x={liftEndX/2 - 50} y={liftEndY - 6} fill={C.cyan} fontSize="11" fontFamily="ui-monospace, monospace">L·sin(φ) → centripetal</text>
          {/* The aircraft */}
          <PlaneFromBehind bank={bank} />
          {/* Load factor readout */}
          <g transform="translate(120, 110)">
            <rect x="-50" y="-20" width="100" height="40" fill="#0b0b0d" stroke={C.amber} strokeWidth="1.2" />
            <text x="0" y="-4" textAnchor="middle" fill={C.amber} fontSize="9" fontFamily="ui-monospace, monospace">LOAD FACTOR</text>
            <text x="0" y="14" textAnchor="middle" fill={C.amber} fontSize="18" fontFamily="ui-monospace, monospace" fontWeight="bold">1.15 G</text>
          </g>
          <defs>
            <marker id="arrL" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill={C.amber} /></marker>
            <marker id="arrW" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill={C.rose} /></marker>
          </defs>
        </svg>
      </div>
    </Frame>
  );
}

function AdverseYaw() {
  // Right-turn entry. Rising left wing has MORE drag → nose yaws LEFT (away from turn).
  // Show pulsing drag arrows + a rudder-in arrow countering it.
  const step = useStep(3, 1400);
  return (
    <Frame label="ADVERSE AILERON YAW">
      <div className="absolute inset-0 flex items-center justify-center">
        <svg viewBox="-200 -130 400 260" className="w-full h-full">
          {/* horizon */}
          <line x1="-200" y1="60" x2="200" y2="60" stroke={C.cyan} strokeOpacity="0.25" strokeDasharray="3 3" />
          {/* Top-down aircraft */}
          <g>
            {/* fuselage (pointing UP = forward) */}
            <ellipse cx="0" cy="0" rx="8" ry="42" fill="#0b0b0d" stroke={C.amber} strokeWidth="1.4" />
            {/* wings */}
            <rect x="-130" y="-6" width="260" height="12" fill={C.amber} opacity="0.9" rx="2" />
            {/* tail */}
            <rect x="-22" y="34" width="44" height="6" fill={C.amber} />
            {/* Ailerons — left wing UP (rising), right wing DOWN (dropping) for a RIGHT turn */}
            <rect x="-130" y="-6" width="50" height="12" fill={step >= 0 ? C.green : C.amber} opacity={step >= 0 ? 0.8 : 0.4} />
            <rect x="80" y="-6" width="50" height="12" fill={step >= 0 ? C.rose : C.amber} opacity={step >= 0 ? 0.8 : 0.4} />
          </g>
          {/* Drag arrows (left wing more drag → yaws LEFT) */}
          {step >= 1 && (
            <>
              <line x1="-105" y1="-30" x2="-105" y2="-70" stroke={C.green} strokeWidth="3" markerEnd="url(#yawArrG)" />
              <text x="-100" y="-78" fill={C.green} fontSize="11" fontFamily="ui-monospace, monospace">MORE drag</text>
              <line x1="105" y1="-22" x2="105" y2="-46" stroke={C.rose} strokeWidth="2" markerEnd="url(#yawArrR)" />
              <text x="58" y="-50" fill={C.rose} fontSize="11" fontFamily="ui-monospace, monospace">less drag</text>
            </>
          )}
          {/* The yaw (nose swinging away from turn) */}
          {step >= 1 && (
            <path d="M 0 -50 Q -30 -78 -70 -88" stroke={C.rose} strokeWidth="2.5" fill="none" markerEnd="url(#yawCurve)" strokeDasharray="0" />
          )}
          {/* Rudder correction */}
          {step >= 2 && (
            <g>
              <path d="M 0 30 Q 30 60 70 80" stroke={C.cyan} strokeWidth="2.5" fill="none" markerEnd="url(#yawCurveC)" />
              <text x="40" y="100" fill={C.cyan} fontSize="11" fontFamily="ui-monospace, monospace" fontWeight="bold">RUDDER → into the turn</text>
            </g>
          )}
          <defs>
            <marker id="yawArrG" markerWidth="8" markerHeight="8" refX="4" refY="6" orient="auto"><path d="M0,0 L8,6 L0,12 z" fill={C.green} /></marker>
            <marker id="yawArrR" markerWidth="8" markerHeight="8" refX="4" refY="6" orient="auto"><path d="M0,0 L8,6 L0,12 z" fill={C.rose} /></marker>
            <marker id="yawCurve" markerWidth="9" markerHeight="9" refX="6" refY="4" orient="auto"><path d="M0,0 L9,4 L0,8 z" fill={C.rose} /></marker>
            <marker id="yawCurveC" markerWidth="9" markerHeight="9" refX="6" refY="4" orient="auto"><path d="M0,0 L9,4 L0,8 z" fill={C.cyan} /></marker>
          </defs>
        </svg>
      </div>
    </Frame>
  );
}

function BalanceBankBack() {
  const step = useStep(4, 1300);
  const steps = [
    { id: "BALANCE",       sub: "Rudder, same side as bank",    color: C.cyan },
    { id: "BANK",          sub: "Aileron to 30° (or 45°)",      color: C.amber },
    { id: "BACK PRESSURE", sub: "Hold altitude",                color: C.rose },
    { id: "+POWER",        sub: "Steep only — add power",       color: C.green },
  ];
  return (
    <Frame label="ENTRY SEQUENCE">
      <div className="absolute inset-0 flex items-center justify-center p-6">
        <div className="space-y-3 w-full max-w-xl">
          {steps.map((s, i) => {
            const active = step === i;
            return (
              <div key={s.id} className="grid grid-cols-12 items-center gap-3" style={{ opacity: active ? 1 : 0.45, transition: "opacity 0.3s" }}>
                <div className="col-span-1 font-mono text-xs" style={{ color: s.color }}>{i + 1}.</div>
                <div className="col-span-4 font-heading text-2xl tracking-wider" style={{ color: s.color, textShadow: active ? `0 0 12px ${s.color}` : "none" }}>{s.id}</div>
                <div className="col-span-7 font-mono text-sm text-slate-700">{s.sub}</div>
              </div>
            );
          })}
        </div>
      </div>
    </Frame>
  );
}

function SpiralDive() {
  const step = useStep(4, 1500);
  const items = [
    { k: "POWER",     v: "IDLE",      color: C.rose },
    { k: "WINGS",     v: "LEVEL",     color: C.cyan },
    { k: "EASE OUT",  v: "OF DIVE",   color: C.amber },
    { k: "POWER",     v: "INCREASE",  color: C.green },
  ];
  return (
    <Frame label="SPIRAL DIVE RECOVERY">
      <div className="absolute inset-0 grid grid-cols-2">
        {/* Left: schematic */}
        <div className="relative">
          <svg viewBox="-100 -100 200 200" className="w-full h-full">
            <line x1="-100" y1="0" x2="100" y2="0" stroke={C.cyan} strokeOpacity="0.3" strokeDasharray="3 3" />
            {/* spiral path */}
            <path d="M -60 -80 Q -10 -40 30 0 Q 60 30 0 70" fill="none" stroke={C.rose} strokeWidth="2" strokeDasharray={step < 2 ? "0" : "4 4"} />
            <PlaneFromBehind bank={step < 2 ? 70 : step === 2 ? 20 : 0} size={120} color={step < 2 ? C.rose : C.amber} />
          </svg>
        </div>
        {/* Right: checklist */}
        <div className="flex flex-col justify-center p-6 gap-3">
          {items.map((it, i) => {
            const active = step === i;
            const done = step > i;
            return (
              <div key={i} className="flex items-center gap-3" style={{ opacity: active ? 1 : (done ? 0.5 : 0.3), transition: "opacity 0.3s" }}>
                <div className="w-6 h-6 border flex items-center justify-center font-mono text-xs" style={{ borderColor: it.color, color: it.color }}>{done ? "✓" : i + 1}</div>
                <div className="font-heading text-lg tracking-wider text-slate-800">{it.k}</div>
                <div className="font-mono text-sm" style={{ color: it.color }}>{it.v}</div>
              </div>
            );
          })}
        </div>
      </div>
    </Frame>
  );
}

function SteepBank({ bank = 45 }) {
  const tilt = (bank * Math.PI) / 180;
  const liftMag = 160;
  const lx = Math.sin(tilt) * liftMag;
  const ly = -Math.cos(tilt) * liftMag;
  const g = bank === 60 ? "2.00" : bank === 45 ? "1.41" : "1.15";
  return (
    <Frame label={`STEEP TURN // ${bank}° BANK`}>
      <div className="absolute inset-0 flex items-center justify-center">
        <svg viewBox="-220 -200 440 340" className="w-full h-full">
          <line x1="-220" y1="0" x2="220" y2="0" stroke={C.cyan} strokeOpacity="0.3" strokeDasharray="4 4" />
          <line x1="0" y1="0" x2="0" y2="140" stroke={C.rose} strokeWidth="2.5" markerEnd="url(#sArrW)" />
          <text x="6" y="138" fill={C.rose} fontSize="14" fontFamily="ui-monospace, monospace">W</text>
          <line x1="0" y1="0" x2={lx} y2={ly} stroke={C.amber} strokeWidth="3" markerEnd="url(#sArrL)" />
          <text x={lx + 6} y={ly} fill={C.amber} fontSize="14" fontFamily="ui-monospace, monospace">L (1.41× greater)</text>
          <line x1="0" y1="0" x2="0" y2={ly} stroke={C.green} strokeWidth="2" strokeDasharray="5 4" />
          <line x1="0" y1={ly} x2={lx} y2={ly} stroke={C.cyan} strokeWidth="2" strokeDasharray="5 4" />
          <PlaneFromBehind bank={bank} />
          <g transform="translate(120, 130)">
            <rect x="-60" y="-22" width="120" height="44" fill="#0b0b0d" stroke={C.amber} strokeWidth="1.4" />
            <text x="0" y="-6" textAnchor="middle" fill={C.amber} fontSize="9" fontFamily="ui-monospace, monospace">LOAD FACTOR</text>
            <text x="0" y="14" textAnchor="middle" fill={C.amber} fontSize="22" fontFamily="ui-monospace, monospace" fontWeight="bold">{g} G</text>
          </g>
          <defs>
            <marker id="sArrL" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill={C.amber} /></marker>
            <marker id="sArrW" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8 z" fill={C.rose} /></marker>
          </defs>
        </svg>
      </div>
    </Frame>
  );
}

function StallSpeedCurve() {
  // Vss(φ) = Vs · sqrt(1/cos(φ))
  const Vs = 44;
  const data = [0, 15, 30, 45, 60, 70].map((deg) => ({
    deg,
    v: +(Vs / Math.sqrt(Math.cos((deg * Math.PI) / 180))).toFixed(1),
  }));
  const step = useStep(data.length, 900);
  // Map to SVG coords (chart x: 0..400, y: 0..200 inverted)
  const xFor = (deg) => (deg / 70) * 380 + 10;
  const yFor = (v) => 200 - ((v - 40) / 40) * 160; // map 40→200, 80→40
  const points = data.map((d) => `${xFor(d.deg)},${yFor(d.v)}`).join(" ");
  return (
    <Frame label="STALL SPEED vs BANK ANGLE (C172, CLEAN)">
      <div className="absolute inset-0 flex items-center justify-center p-3">
        <svg viewBox="0 -10 400 230" className="w-full h-full">
          {/* axes */}
          <line x1="10" y1="200" x2="400" y2="200" stroke={C.cream} strokeOpacity="0.5" />
          <line x1="10" y1="0" x2="10" y2="200" stroke={C.cream} strokeOpacity="0.5" />
          <text x="200" y="220" textAnchor="middle" fill={C.cream} fontSize="10" fontFamily="ui-monospace, monospace">Bank Angle φ (deg)</text>
          <text x="-100" y="20" transform="rotate(-90)" textAnchor="middle" fill={C.cream} fontSize="10" fontFamily="ui-monospace, monospace">Stall Speed (kts)</text>
          {/* gridlines + tick labels */}
          {[0, 30, 45, 60, 70].map((d) => (
            <g key={d}>
              <line x1={xFor(d)} y1="0" x2={xFor(d)} y2="200" stroke={C.cream} strokeOpacity="0.08" />
              <text x={xFor(d)} y="212" textAnchor="middle" fill={C.cream} fontSize="9" fontFamily="ui-monospace, monospace">{d}°</text>
            </g>
          ))}
          {/* curve drawn progressively */}
          <polyline
            points={points.split(" ").slice(0, step + 1).join(" ")}
            fill="none" stroke={C.amber} strokeWidth="2.5"
          />
          {/* dots + labels */}
          {data.slice(0, step + 1).map((d, i) => (
            <g key={i}>
              <circle cx={xFor(d.deg)} cy={yFor(d.v)} r="3.5" fill={C.amber} />
              <text x={xFor(d.deg) + 6} y={yFor(d.v) - 6} fill={C.amber} fontSize="11" fontFamily="ui-monospace, monospace">{d.v} kt</text>
            </g>
          ))}
        </svg>
      </div>
    </Frame>
  );
}

function PowerSandwich() {
  const step = useStep(4, 1200);
  // "Power IN at the bank, power OUT at roll-out"
  const phases = [
    { t: "ROLL IN",     sub: "Power → INCREASE",  color: C.green,  pos: -180 },
    { t: "AT 45°",      sub: "Hold ALT + AS",     color: C.amber,  pos: -60 },
    { t: "STILL AT 45°",sub: "Hold ALT + AS",     color: C.amber,  pos: 60 },
    { t: "ROLL OUT",    sub: "Power → REDUCE",    color: C.rose,   pos: 180 },
  ];
  return (
    <Frame label="THE POWER SANDWICH">
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <svg viewBox="-220 -120 440 240" className="w-full h-full">
          {/* horizon line */}
          <line x1="-220" y1="50" x2="220" y2="50" stroke={C.cyan} strokeOpacity="0.25" strokeDasharray="3 3" />
          {/* the 4 aircraft positions banking up to 45° in the middle two */}
          {phases.map((p, i) => (
            <g key={i} transform={`translate(${p.pos}, 0)`} style={{ opacity: step >= i ? 1 : 0.2, transition: "opacity 0.3s" }}>
              <PlaneFromBehind bank={(i === 1 || i === 2) ? 45 : 0} size={70} color={step === i ? C.amber : (step > i ? C.green : C.cream)} glow={false} />
              <text x="0" y="60" textAnchor="middle" fill={p.color} fontSize="9" fontFamily="ui-monospace, monospace" fontWeight="bold">{p.t}</text>
              <text x="0" y="74" textAnchor="middle" fill={p.color} fontSize="9" fontFamily="ui-monospace, monospace">{p.sub}</text>
            </g>
          ))}
        </svg>
      </div>
    </Frame>
  );
}

function DescendingSteep() {
  const step = useStep(3, 1500);
  return (
    <Frame label="DESCENDING STEEP TURN">
      <div className="absolute inset-0 flex items-center justify-center">
        <svg viewBox="-200 -150 400 300" className="w-full h-full">
          <line x1="-200" y1="0" x2="200" y2="0" stroke={C.cyan} strokeOpacity="0.3" strokeDasharray="4 4" />
          <text x="-180" y="-6" fill={C.cyan} fontSize="10" fontFamily="ui-monospace, monospace">HORIZON</text>
          {/* nose-down + banked aircraft */}
          <g transform="translate(0, -20) rotate(-15)">
            <PlaneFromBehind bank={45} size={170} color={C.amber} />
          </g>
          {/* Descent arrow */}
          <line x1="-130" y1="-30" x2="-130" y2="120" stroke={C.rose} strokeWidth="2.5" markerEnd="url(#descArr)" />
          <text x="-118" y="60" fill={C.rose} fontSize="11" fontFamily="ui-monospace, monospace">DESCENT RATE</text>
          {/* IAS readout */}
          <g transform="translate(130, 80)">
            <rect x="-50" y="-22" width="100" height="44" fill="#0b0b0d" stroke={C.amber} strokeWidth="1.2" />
            <text x="0" y="-6" textAnchor="middle" fill={C.amber} fontSize="9" fontFamily="ui-monospace, monospace">TARGET IAS</text>
            <text x="0" y="14" textAnchor="middle" fill={C.amber} fontSize="18" fontFamily="ui-monospace, monospace" fontWeight="bold">95 kt</text>
          </g>
          {/* Power readout */}
          <g transform="translate(-130, 90)">
            <rect x="-50" y="-22" width="100" height="44" fill="#0b0b0d" stroke={C.rose} strokeWidth="1.2" />
            <text x="0" y="-6" textAnchor="middle" fill={C.rose} fontSize="9" fontFamily="ui-monospace, monospace">POWER</text>
            <text x="0" y="14" textAnchor="middle" fill={C.rose} fontSize="18" fontFamily="ui-monospace, monospace" fontWeight="bold">IDLE</text>
          </g>
          <defs>
            <marker id="descArr" markerWidth="9" markerHeight="9" refX="6" refY="4" orient="auto"><path d="M0,0 L9,4 L0,8 z" fill={C.rose} /></marker>
          </defs>
          {/* pulsing radial indicator */}
          {step >= 1 && (
            <circle cx="0" cy="0" r={50 + step * 12} fill="none" stroke={C.amber} strokeWidth="0.6" opacity={0.5 - step * 0.15} />
          )}
        </svg>
      </div>
    </Frame>
  );
}
